import {ChecksStatusBadge} from '@github-ui/commit-checks-status'
import {useAlive} from '@github-ui/use-alive'
import useIsMounted from '@github-ui/use-is-mounted'
import throttle from 'lodash-es/throttle'
import {useCallback, useEffect, useMemo} from 'react'

import {useLiveCommitChecksStatus} from '../../hooks/use-live-commit-checks-status'
import styles from './ChecksStatusButton.module.css'

interface ChecksStatusButtonProps {
  headSha: string
  repositoryNwo: string
  commitAliveChannel?: string
}

export function ChecksStatusButton({headSha, repositoryNwo, commitAliveChannel}: ChecksStatusButtonProps) {
  const [ownerLogin = '', name = ''] = repositoryNwo?.split('/') ?? []
  const repo = useMemo(() => ({ownerLogin, name}), [ownerLogin, name])

  const [checksDetails, fetchChecksDetails] = useLiveCommitChecksStatus(headSha, repo)

  const isMounted = useIsMounted()
  const throttledFetchChecksDetails = useMemo(
    () =>
      throttle(
        () => {
          if (isMounted()) fetchChecksDetails()
        },
        2000,
        {trailing: true},
      ),
    [fetchChecksDetails, isMounted],
  )
  useEffect(() => () => throttledFetchChecksDetails.cancel(), [throttledFetchChecksDetails])
  const handleAliveUpdate = useCallback(
    (data: {wait?: number}) => {
      window.setTimeout(() => throttledFetchChecksDetails(), data.wait ?? 0)
    },
    [throttledFetchChecksDetails],
  )

  useAlive(commitAliveChannel, handleAliveUpdate)

  // Fetch initial data when component mounts
  useEffect(() => {
    if (headSha && repositoryNwo) {
      fetchChecksDetails()
    }
  }, [headSha, repositoryNwo, fetchChecksDetails])

  // Don't render if no required data or no checks data loaded yet
  const hasPendingApprovals =
    checksDetails?.pendingWorkflowApproval && checksDetails.pendingWorkflowApproval.workflowsRequiringApprovalCount > 0
  if (!headSha || !repositoryNwo || !checksDetails || (checksDetails.checkRuns.length === 0 && !hasPendingApprovals)) {
    return null
  }

  const getCheckCountsText = () => {
    if (!checksDetails?.checkRuns) return ''

    const totalChecks = checksDetails.checkRuns.length
    const successfulChecks = checksDetails.checkRuns.filter(run => run.state === 'success').length

    return `${successfulChecks}/${totalChecks}`
  }

  const mapHeaderStateToStatusRollup = (headerState: string) => {
    switch (headerState) {
      case 'SUCCEEDED':
        return 'success'
      case 'FAILED':
        return 'failure'
      case 'PENDING':
        return 'pending'
      case 'UNSUCCESSFUL':
        return 'failure'
      default:
        return 'pending'
    }
  }

  return (
    <span className={styles.checksButton}>
      <ChecksStatusBadge
        statusRollup={mapHeaderStateToStatusRollup(checksDetails?.checksHeaderState || '')}
        combinedStatus={checksDetails}
        onWillOpenPopup={fetchChecksDetails}
        size="small"
        variant="default"
        buttonClassName={styles.checksCountText}
        descriptionText={getCheckCountsText()}
        repositoryNwo={repositoryNwo}
      />
    </span>
  )
}
